/** Lo que se extrae de una URL de reporte de Power BI */
export interface PowerBiUrlParts {
  /** URL de embebido (origen + /reportEmbed), que es lo que se guarda */
  serverUrl: string;
  reportId: string | null;
  /** Workspace; null si la URL no trae uno (p. ej. "groups/me") */
  groupId: string | null;
  /** Tenant */
  ctid: string | null;
}

/** Motivo por el que no se pudo leer la URL */
export type PowerBiUrlError = 'INVALID_URL' | 'MISSING_REPORT_ID';

export interface PowerBiUrlResult {
  parts: PowerBiUrlParts | null;
  error: PowerBiUrlError | null;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Extrae de la URL de Power BI el id del reporte, el del workspace y el del
 * tenant. Soporta los dos formatos que entrega Power BI:
 *  - https://app.powerbi.com/groups/{groupId}/reports/{reportId}/{pagina}?ctid={ctid}
 *  - https://app.powerbi.com/reportEmbed?reportId={reportId}&groupId={groupId}&ctid={ctid}
 * @param value URL pegada por el usuario; vacía devuelve parts y error en null
 */
export function parsePowerBiUrl(value: string): PowerBiUrlResult {
  const raw = (value || '').trim();
  if (!raw) return { parts: null, error: null };

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return { parts: null, error: 'INVALID_URL' };
  }

  const segments = url.pathname.split('/').filter((segment) => !!segment);
  const afterSegment = (name: string): string => {
    const index = segments.findIndex((segment) => segment.toLowerCase() === name);
    return index >= 0 && index + 1 < segments.length ? segments[index + 1] : '';
  };

  const reportId = url.searchParams.get('reportId') || afterSegment('reports');
  const groupId = url.searchParams.get('groupId') || afterSegment('groups');
  const ctid = url.searchParams.get('ctid') || '';

  const parts: PowerBiUrlParts = {
    serverUrl: `${url.origin}/reportEmbed`,
    reportId: reportId || null,
    // "groups/me" no es un workspace, por eso solo se toma si es un guid
    groupId: GUID.test(groupId.trim()) ? groupId : null,
    ctid: ctid || null
  };

  return { parts, error: reportId ? null : 'MISSING_REPORT_ID' };
}

/**
 * Rearma la URL de embebido a partir de los ids guardados (para el modo edición)
 * @param serverUrl URL base guardada
 * @param reportId Id del reporte; sin él se devuelve serverUrl tal cual
 * @param groupId Id del workspace
 * @param ctid Id del tenant
 */
export function buildPowerBiUrl(serverUrl: string, reportId: string, groupId?: string, ctid?: string): string {
  if (!reportId) return serverUrl || '';

  let url: URL;
  try {
    url = new URL(serverUrl || 'https://app.powerbi.com/reportEmbed');
  } catch {
    // Reportes viejos pueden tener en serverUrl algo que no es una URL
    url = new URL('https://app.powerbi.com/reportEmbed');
  }
  url.searchParams.set('reportId', reportId);
  if (groupId) url.searchParams.set('groupId', groupId);
  if (ctid) url.searchParams.set('ctid', ctid);
  return url.toString();
}
